import { initAnimationsForElement } from '../utils/animations.js';

const SUBMIT_ENDPOINT = '/api/submit-migration-playbook-lead';
const TOTAL_STEPS = 5;

const PHASE_STATUS_META = {
  ready: { label: 'Ready', statusClass: 'success', itemClass: 'ready' },
  'in-progress': { label: 'In progress', statusClass: 'warning', itemClass: 'in-progress' },
  blocked: { label: 'Blocked', statusClass: 'danger', itemClass: 'blocked' },
  upcoming: { label: 'Up next', statusClass: 'muted', itemClass: 'upcoming' },
};

class MigrationPlaybook extends HTMLElement {
  #elements = {};
  #platformData = null;
  #scoringData = null;
  #currentStep = 1;
  #selectedPlatform = null;
  #lastAssessment = null;
  #isSubmitting = false;

  connectedCallback() {
    this.#loadData();
    this.#cacheElements();
    this.#renderPlatformGrid();
    this.#renderCheckboxGrids();
    this.#renderPhaseRoadmap();
    this.#bindEvents();
    this.#applyQueryPlatform();
    this.#recalculate();

    requestAnimationFrame(() => {
      initAnimationsForElement(this);
    });
  }

  #loadData() {
    const platformsEl = this.querySelector('#migration-platforms-data');
    const scoringEl = this.querySelector('#migration-scoring-data');

    this.#platformData = this.#parseJsonScript(platformsEl, { platforms: [], phases: [] });
    this.#scoringData = this.#parseJsonScript(scoringEl, {});
  }

  #parseJsonScript(element, fallback) {
    if (!element) return fallback;

    const raw = element.textContent?.trim();
    if (!raw) return fallback;

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error('Migration playbook failed to parse embedded data:', error);
      return fallback;
    }
  }

  #cacheElements() {
    const ids = [
      'display_score', 'display_tier', 'display_timeline', 'score_fill',
      'platform_warnings', 'platform_blog_link', 'platform_guide_link',
      'recommended_actions', 'phase_roadmap',
      'gmv_band', 'sku_count', 'markets', 'integration_count',
      'url_count', 'content_volume', 'redirect_map', 'international',
      'dev_capacity', 'target_timeline', 'risk_tolerance', 'must_haves',
      'print_date', 'print_requestor_name', 'print_shopify_url',
      'print_platform', 'print_score', 'print_tier', 'print_timeline',
      'p_gmv', 'p_sku', 'p_markets', 'p_channels',
      'p_data_score', 'p_integration_score', 'p_seo_score',
      'p_platform_score', 'p_execution_score',
      'print_warnings', 'print_phases', 'print_actions',
      'print_name', 'print_email', 'print_shopify_url_input',
      'print_company_website', 'print_submit_status',
      'tt_print_modal', 'tt_print_form',
    ];

    for (const id of ids) {
      this.#elements[this.#toCamelCase(id)] = this.querySelector(`#${id}`);
    }
  }

  #toCamelCase(id) {
    return id.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  }

  #renderPlatformGrid() {
    const grid = this.querySelector('#platform_grid');
    if (!grid || !this.#platformData?.platforms) return;

    grid.innerHTML = this.#platformData.platforms.map((platform) => `
      <button type="button" class="mp-platform-btn" data-platform="${platform.id}" aria-pressed="false">
        <span class="mp-platform-btn-name">${platform.shortName}</span>
      </button>
    `).join('');
  }

  #renderCheckboxGrids() {
    const channelsGrid = this.querySelector('#channels_grid');
    const integrationsGrid = this.querySelector('#integrations_grid');

    if (channelsGrid && this.#scoringData?.channels) {
      channelsGrid.innerHTML = Object.entries(this.#scoringData.channels).map(([key, item]) => `
        <label class="mp-checkbox-label">
          <input type="checkbox" class="tt-form-checkbox" data-channel="${key}" ${key === 'd2c' ? 'checked' : ''}>
          <span>${item.label}</span>
        </label>
      `).join('');
    }

    if (integrationsGrid && this.#scoringData?.integrations) {
      integrationsGrid.innerHTML = Object.entries(this.#scoringData.integrations).map(([key, item]) => `
        <label class="mp-checkbox-label">
          <input type="checkbox" class="tt-form-checkbox" data-integration="${key}">
          <span>${item.label}</span>
        </label>
      `).join('');
    }
  }

  #renderPhaseRoadmap(phaseStatuses = []) {
    const list = this.#elements.phaseRoadmap;
    if (!list || !this.#platformData?.phases) return;

    const statusMap = new Map(phaseStatuses.map((phase) => [phase.id, phase]));

    list.innerHTML = this.#platformData.phases.map((phase, index) => {
      const phaseStatus = statusMap.get(phase.id);
      const meta = PHASE_STATUS_META[phaseStatus?.status] || PHASE_STATUS_META.upcoming;

      return `
        <li class="mp-phase-item mp-phase-item--${meta.itemClass}" data-phase="${phase.id}">
          <span class="mp-phase-num">${index + 1}</span>
          <div class="mp-phase-content">
            <div class="mp-phase-heading">
              <strong>${phase.name}</strong>
              <span class="mp-phase-status tt-status tt-status--${meta.statusClass}">${meta.label}</span>
            </div>
            <p class="text-gray small no-margin-bottom">${phase.description}</p>
          </div>
        </li>
      `;
    }).join('');
  }

  #bindEvents() {
    for (const button of this.querySelectorAll('[data-platform]')) {
      button.addEventListener('click', () => {
        this.#selectPlatform(button.dataset.platform);
      });
    }

    for (const nav of this.querySelectorAll('[data-step-nav]')) {
      nav.addEventListener('click', () => {
        const step = Number(nav.dataset.stepNav);
        if (step <= this.#currentStep || this.#selectedPlatform) {
          this.#goToStep(step);
        }
      });
    }

    this.querySelector('[data-action="next-step"]')?.addEventListener('click', () => {
      if (this.#currentStep >= TOTAL_STEPS) {
        if (!this.#selectedPlatform) {
          this.#goToStep(1);
          return;
        }
        this.#openPrintModal();
        return;
      }
      this.#goToStep(this.#currentStep + 1);
    });

    this.querySelector('[data-action="prev-step"]')?.addEventListener('click', () => {
      if (this.#currentStep > 1) {
        this.#goToStep(this.#currentStep - 1);
      }
    });

    this.querySelector('[data-action="download-roadmap"]')?.addEventListener('click', () => {
      if (!this.#selectedPlatform) {
        this.#goToStep(1);
        return;
      }
      this.#openPrintModal();
    });

    this.#elements.ttPrintForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.#handlePrintSubmit();
    });

    for (const button of this.querySelectorAll('[data-action="close-print-modal"]')) {
      button.addEventListener('click', () => {
        this.#elements.ttPrintModal?.close();
      });
    }

    this.#elements.ttPrintModal?.addEventListener('click', (event) => {
      if (event.target === this.#elements.ttPrintModal) {
        this.#elements.ttPrintModal.close();
      }
    });

    window.addEventListener('afterprint', () => {
      document.body.classList.remove('tt-printing');
    });

    const inputs = this.querySelectorAll('select, input[type="checkbox"], textarea');
    for (const input of inputs) {
      input.addEventListener('change', () => this.#recalculate());
      input.addEventListener('input', () => this.#recalculate());
    }
  }

  #applyQueryPlatform() {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('platform');
    const fromHash = this.#parsePlatformHash(window.location.hash);
    const platform = fromQuery || fromHash;

    if (!platform) return;

    if (fromQuery) {
      const url = new URL(window.location.href);
      url.searchParams.delete('platform');
      url.hash = `platform=${platform}`;
      window.history.replaceState(null, '', `${url.pathname}${url.hash}`);
    }

    this.#selectPlatform(platform);
  }

  #parsePlatformHash(hash) {
    const match = hash.match(/^#platform=([a-z0-9-]+)$/i);
    return match ? match[1] : null;
  }

  #selectPlatform(platformId) {
    const platform = this.#platformData?.platforms?.find((item) => item.id === platformId);
    if (!platform) return;

    this.#selectedPlatform = platform;

    for (const button of this.querySelectorAll('[data-platform]')) {
      const isActive = button.dataset.platform === platformId;
      button.classList.toggle('mp-platform-btn--active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    this.#updatePlatformWarnings(platform);
    this.#recalculate();
  }

  #goToStep(step) {
    if (step === 2 && !this.#selectedPlatform) {
      return;
    }

    this.#currentStep = step;

    for (const panel of this.querySelectorAll('.mp-step-panel')) {
      panel.classList.toggle('hide', Number(panel.dataset.step) !== step);
    }

    for (const nav of this.querySelectorAll('[data-step-nav]')) {
      const navStep = Number(nav.dataset.stepNav);
      nav.classList.toggle('mp-step-item--active', navStep === step);
      nav.classList.toggle('mp-step-item--done', navStep < step);
    }

    const prevBtn = this.querySelector('[data-action="prev-step"]');
    const nextBtn = this.querySelector('[data-action="next-step"]');

    if (prevBtn) prevBtn.disabled = step === 1;
    if (nextBtn) {
      const isFinalStep = step === TOTAL_STEPS;
      const nextLabel = isFinalStep ? 'Download Roadmap' : 'Next step';
      nextBtn.querySelector('div')?.setAttribute('data-hover', nextLabel);
      if (nextBtn.firstElementChild) {
        nextBtn.firstElementChild.textContent = nextLabel;
      }
      nextBtn.classList.toggle('btn-grad', isFinalStep);
    }

    if (this.#selectedPlatform) {
      this.#recalculate();
    }
  }

  #getFormState() {
    const channels = [...this.querySelectorAll('[data-channel]:checked')].map((el) => el.dataset.channel);
    const integrations = [...this.querySelectorAll('[data-integration]:checked')].map((el) => el.dataset.integration);

    return {
      platform: this.#selectedPlatform?.id || null,
      platformName: this.#selectedPlatform?.name || null,
      gmvBand: this.#elements.gmvBand?.value || '2m-10m',
      skuCount: this.#elements.skuCount?.value || '500-2000',
      markets: this.#elements.markets?.value || '1',
      channels,
      integrationCount: this.#elements.integrationCount?.value || '4-8',
      integrations,
      urlCount: this.#elements.urlCount?.value || '500-2000',
      contentVolume: this.#elements.contentVolume?.value || 'moderate',
      redirectMap: this.#elements.redirectMap?.value || 'no',
      international: this.#elements.international?.checked || false,
      devCapacity: this.#elements.devCapacity?.value || 'limited',
      targetTimeline: this.#elements.targetTimeline?.value || '8-16',
      riskTolerance: this.#elements.riskTolerance?.value || 'low',
      mustHaves: this.#elements.mustHaves?.value?.trim() || '',
    };
  }

  #recalculate() {
    if (!this.#selectedPlatform) return;

    const state = this.#getFormState();
    const assessment = this.#calculateAssessment(state);
    this.#lastAssessment = { ...state, ...assessment };
    this.#updateOutputs(assessment, state);
    this.#syncPrintLayout(this.#lastAssessment);
  }

  #calculateAssessment(state) {
    const opts = this.#scoringData.options || {};
    const integrations = this.#scoringData.integrations || {};
    const channels = this.#scoringData.channels || {};

    let totalComplexity = this.#selectedPlatform.complexityPenalty || 0;

    totalComplexity += opts.gmv?.[state.gmvBand]?.complexity || 0;
    totalComplexity += opts.skuCount?.[state.skuCount]?.complexity || 0;
    totalComplexity += opts.markets?.[state.markets]?.complexity || 0;
    totalComplexity += opts.urlCount?.[state.urlCount]?.complexity || 0;
    totalComplexity += opts.contentVolume?.[state.contentVolume]?.complexity || 0;
    totalComplexity += opts.integrationCount?.[state.integrationCount]?.complexity || 0;

    for (const key of state.channels) {
      totalComplexity += channels[key]?.complexity || 0;
    }

    for (const key of state.integrations) {
      totalComplexity += integrations[key]?.complexity || 0;
    }

    let seoRisk = 0;
    const redirectOpt = opts.redirectMap?.[state.redirectMap];
    if (redirectOpt?.seoRisk) seoRisk += redirectOpt.seoRisk;
    if (state.international) seoRisk += 10;
    if (state.urlCount === '2000-10000' || state.urlCount === '10000-plus') seoRisk += 8;

    let executionRisk = 0;
    executionRisk += opts.devCapacity?.[state.devCapacity]?.executionRisk || 0;
    executionRisk += opts.targetTimeline?.[state.targetTimeline]?.executionRisk || 0;
    executionRisk += opts.riskTolerance?.[state.riskTolerance]?.executionRisk || 0;

    totalComplexity += seoRisk + executionRisk;

    let readinessBonus = redirectOpt?.readinessBonus || 0;
    if (state.devCapacity === 'dedicated') readinessBonus += 5;
    if (state.targetTimeline === '16-plus' || state.targetTimeline === 'flexible') readinessBonus += 3;

    const readinessScale = this.#scoringData.readinessScale ?? 1;
    const adjustedComplexity = Math.round(totalComplexity * readinessScale);
    const readinessScore = Math.max(0, Math.min(100, Math.round(100 - adjustedComplexity + readinessBonus)));

    const dataScore = Math.min(100, Math.round(
      (opts.skuCount?.[state.skuCount]?.complexity || 0) * 2
      + (opts.urlCount?.[state.urlCount]?.complexity || 0) * 1.5
      + (opts.markets?.[state.markets]?.complexity || 0) * 2
      + (opts.contentVolume?.[state.contentVolume]?.complexity || 0) * 1.5
    ));

    const integrationScore = Math.min(100, Math.round(
      (opts.integrationCount?.[state.integrationCount]?.complexity || 0) * 2
      + state.integrations.reduce((sum, key) => sum + (integrations[key]?.complexity || 0), 0) * 2
    ));

    const seoScore = Math.min(100, seoRisk * 2);
    const platformScore = Math.min(100, (this.#selectedPlatform.complexityPenalty || 0) * 2);
    const executionScore = Math.min(100, executionRisk * 2);

    const tier = this.#scoringData.tiers?.find((item) => readinessScore >= item.min)
      || this.#scoringData.tiers?.[this.#scoringData.tiers.length - 1];

    const timelineBand = this.#scoringData.timelineBands?.find(
      (band) => adjustedComplexity >= band.minComplexity && adjustedComplexity <= band.maxComplexity,
    ) || this.#scoringData.timelineBands?.[this.#scoringData.timelineBands.length - 1];

    const dimensionScores = [
      { key: 'data', score: dataScore },
      { key: 'integration', score: integrationScore },
      { key: 'seo', score: seoScore },
      { key: 'platform', score: platformScore },
      { key: 'execution', score: executionScore },
    ].sort((a, b) => b.score - a.score);

    const actions = [];
    const dimensionActions = this.#scoringData.dimensionActions || {};

    for (const dim of dimensionScores.slice(0, 3)) {
      const dimActions = dimensionActions[dim.key] || [];
      if (dimActions[0]) actions.push(dimActions[0]);
    }

    if (state.redirectMap === 'no') {
      actions.push('Start building your 301 redirect map before you begin theme development.');
    }

    const recommendedActions = [...new Set(actions)].slice(0, 5);

    const phaseStatuses = this.#calculatePhaseStatuses(readinessScore, dimensionScores, state);

    return {
      readinessScore,
      readinessTier: tier?.label || 'Unknown',
      tierStatusClass: tier?.statusClass || 'warning',
      tierSummary: tier?.summary || '',
      totalComplexity,
      complexityBand: timelineBand?.label || '12–20 weeks',
      timelineEstimate: timelineBand?.label || '12–20 weeks',
      dataScore,
      integrationScore,
      seoScore,
      platformScore,
      executionScore,
      recommendedActions,
      phaseStatuses,
    };
  }

  #calculatePhaseStatuses(readinessScore, dimensionScores, state) {
    const worst = dimensionScores[0]?.key || 'data';
    const phases = this.#platformData?.phases || [];

    const focusByStep = {
      1: 'discovery',
      2: 'data',
      3: 'integrations',
      4: 'seo',
      5: 'build',
    };
    const focusId = focusByStep[this.#currentStep] || 'discovery';
    const focusIndex = phases.findIndex((phase) => phase.id === focusId);

    const blockedPhases = new Set();
    if (worst === 'data' && readinessScore < 60) blockedPhases.add('data');
    if (worst === 'seo' || state.redirectMap === 'no') {
      if (readinessScore < 70) blockedPhases.add('seo');
    }
    if (worst === 'integration' && state.integrations.length >= 2) blockedPhases.add('integrations');
    if (worst === 'platform' && readinessScore < 50) blockedPhases.add('build');
    if (worst === 'execution' && readinessScore < 45) blockedPhases.add('qa');
    if (readinessScore < 35) blockedPhases.add('launch');

    return phases.map((phase, index) => {
      if (blockedPhases.has(phase.id)) {
        return { ...phase, status: 'blocked' };
      }

      if (index < focusIndex) {
        return { ...phase, status: readinessScore >= 55 ? 'ready' : 'in-progress' };
      }

      if (index === focusIndex) {
        return { ...phase, status: 'in-progress' };
      }

      if (readinessScore >= 75 && index === focusIndex + 1) {
        return { ...phase, status: 'in-progress' };
      }

      return { ...phase, status: 'upcoming' };
    });
  }

  #updateOutputs(assessment, state) {
    this.#elements.displayScore.textContent = `${assessment.readinessScore}/100`;
    this.#elements.displayTier.textContent = assessment.readinessTier;
    this.#elements.displayTier.className = `tt-status tt-status--${assessment.tierStatusClass}`;
    this.#elements.scoreFill.style.width = `${assessment.readinessScore}%`;
    this.#elements.displayTimeline.textContent = `Your estimated timeline is ${assessment.timelineEstimate}. ${assessment.tierSummary}`;

    const actionsList = this.#elements.recommendedActions;
    if (actionsList) {
      actionsList.innerHTML = assessment.recommendedActions.map((action) => `<li>${action}</li>`).join('');
    }

    this.#renderPhaseRoadmap(assessment.phaseStatuses);
  }

  #updatePlatformWarnings(platform) {
    const list = this.#elements.platformWarnings;
    if (list) {
      list.innerHTML = (platform.warnings || []).map((warning) => `<li>${warning}</li>`).join('');
    }

    const linkWrap = this.#elements.platformGuideLink;
    const link = this.#elements.platformBlogLink;
    if (link && linkWrap && platform.blogUrl) {
      link.href = platform.blogUrl;
      link.textContent = `Read your ${platform.shortName} migration guide`;
      linkWrap.classList.remove('hide');
    }
  }

  #syncPrintLayout(data) {
    if (!data.platformName) return;

    this.#elements.printDate.textContent = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    this.#elements.printPlatform.textContent = data.platformName;
    this.#elements.printScore.textContent = `${data.readinessScore}/100`;
    this.#elements.printTier.textContent = data.readinessTier;
    this.#elements.printTier.className = `tt-status tt-status--${data.tierStatusClass}`;
    this.#elements.printTimeline.textContent = data.timelineEstimate;

    const opts = this.#scoringData.options || {};
    this.#elements.pGmv.textContent = opts.gmv?.[data.gmvBand]?.label || data.gmvBand;
    this.#elements.pSku.textContent = opts.skuCount?.[data.skuCount]?.label || data.skuCount;
    this.#elements.pMarkets.textContent = opts.markets?.[data.markets]?.label || data.markets;
    this.#elements.pChannels.textContent = data.channels.map((key) => this.#scoringData.channels?.[key]?.label || key).join(', ');

    this.#elements.pDataScore.textContent = `${data.dataScore}/100`;
    this.#elements.pIntegrationScore.textContent = `${data.integrationScore}/100`;
    this.#elements.pSeoScore.textContent = `${data.seoScore}/100`;
    this.#elements.pPlatformScore.textContent = `${data.platformScore}/100`;
    this.#elements.pExecutionScore.textContent = `${data.executionScore}/100`;

    this.#elements.printWarnings.innerHTML = `<ul>${(this.#selectedPlatform?.warnings || []).map((w) => `<li>${w}</li>`).join('')}</ul>`;

    this.#elements.printPhases.innerHTML = `<ol>${(data.phaseStatuses || []).map((phase) => {
      const label = PHASE_STATUS_META[phase.status]?.label || phase.status;
      return `<li><strong>${phase.name}</strong> (${label}) — ${phase.description}</li>`;
    }).join('')}</ol>`;

    this.#elements.printActions.innerHTML = `<ul>${(data.recommendedActions || []).map((a) => `<li>${a}</li>`).join('')}</ul>`;
  }

  #openPrintModal() {
    if (!this.#lastAssessment) this.#recalculate();
    this.#elements.ttPrintModal?.showModal();
    this.#elements.printName?.focus();
  }

  async #handlePrintSubmit() {
    if (this.#isSubmitting) return;

    const form = this.#elements.ttPrintForm;
    const urlInput = this.#elements.printShopifyUrlInput;

    if (urlInput?.value.trim()) {
      urlInput.value = this.#normalizeUrl(urlInput.value.trim());
    }

    if (!form?.checkValidity()) {
      form?.reportValidity();
      return;
    }

    if (this.#elements.printCompanyWebsite?.value.trim()) return;

    const name = this.#elements.printName.value.trim();
    const email = this.#elements.printEmail.value.trim();
    const shopifyUrl = this.#elements.printShopifyUrlInput.value.trim();

    this.#setSubmitStatus('');
    this.#isSubmitting = true;
    this.#setSubmitButtonState(true);

    try {
      await this.#submitToAttio({ name, email, shopifyUrl });
    } catch (error) {
      console.error('Migration playbook Attio submission failed:', error);
      this.#setSubmitStatus('We saved your report locally, but CRM sync failed. You can still print.');
    }

    this.#elements.printRequestorName.textContent = name;
    this.#elements.printShopifyUrl.textContent = shopifyUrl;

    this.#elements.ttPrintModal?.close();
    this.#isSubmitting = false;
    this.#setSubmitButtonState(false);
    document.body.classList.add('tt-printing');
    window.print();
  }

  async #submitToAttio({ name, email, shopifyUrl }) {
    if (!this.#lastAssessment) return;

    const { firstName, lastName } = this.#splitName(name);
    const data = this.#lastAssessment;

    const response = await fetch(SUBMIT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'migration-readiness-assessment',
        tool: 'Shopify Migration Readiness Assessment',
        name,
        first_name: firstName,
        last_name: lastName,
        email,
        shopify_url: shopifyUrl,
        shopify_domain: this.#extractDomain(shopifyUrl),
        platform: data.platform,
        platform_name: data.platformName,
        readiness_score: data.readinessScore,
        readiness_tier: data.readinessTier,
        complexity_band: data.complexityBand,
        timeline_estimate: data.timelineEstimate,
        gmv_band: data.gmvBand,
        sku_count: data.skuCount,
        markets: data.markets,
        channels: data.channels,
        integration_count: data.integrationCount,
        integrations: data.integrations,
        url_count: data.urlCount,
        content_volume: data.contentVolume,
        redirect_map: data.redirectMap,
        international: data.international ? 'yes' : 'no',
        dev_capacity: data.devCapacity,
        target_timeline: data.targetTimeline,
        risk_tolerance: data.riskTolerance,
        must_haves: data.mustHaves,
        data_score: data.dataScore,
        integration_score: data.integrationScore,
        seo_score: data.seoScore,
        platform_score: data.platformScore,
        execution_score: data.executionScore,
        recommended_actions: data.recommendedActions,
        page_url: window.location.href,
        submitted_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Lead submission failed with ${response.status}`);
    }
  }

  #setSubmitButtonState(isSubmitting) {
    const button = this.querySelector('[data-action="submit-print-report"]');
    if (!button) return;
    button.disabled = isSubmitting;
    button.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
  }

  #setSubmitStatus(message) {
    const status = this.#elements.printSubmitStatus;
    if (!status) return;
    if (!message) {
      status.hidden = true;
      status.textContent = '';
      return;
    }
    status.hidden = false;
    status.textContent = message;
  }

  #splitName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
  }

  #extractDomain(url) {
    try {
      return new URL(url).hostname.replace(/^www\./i, '');
    } catch {
      return '';
    }
  }

  #normalizeUrl(url) {
    if (!url) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('//')) return `https:${url}`;
    return `https://${url}`;
  }
}

customElements.define('migration-playbook', MigrationPlaybook);
